/* =============================================================================
   REALTIME — Reservations + Orders + Bon-Archive + Z-Bon-Archive → Cache → UI
   =============================================================================
   Hängt sich beim Login an vier Supabase-Realtime-Publications:
     • reservations  → Reservierungs-Tab + Dashboard
     • orders        → Cockpit-Bestellungs-Tab (Session 11, v0.21.0)
     • bon_archive   → Auswertung/Archiv → Bons (Session 30, Block B)
     • zbon_archive  → Auswertung/Archiv → Z-Bons (Session 30, Block B)
   Eingehende INSERT/UPDATE/DELETE-Events patcht der Storage-Adapter direkt
   in den Cache; wir lösen anschließend ein gedrosseltes Re-Render des
   aktiven Tabs aus und emittieren ein App-Event für Side-Effects (Sound,
   Push-Notification, Counter, Archiv-Tab-Refresh).

   Defensive Behandlung gegen Edit-State-Verlust (Session 6 Bugfix):
   wenn der User gerade tippt oder ein Modal offen ist, defer der Refresh.

   Multi-Tab-Stabilität (Session 30, Block A): die Subscribes laufen jetzt
   durch den Tab-Coordinator (core/tab-coordinator.js). Nur ein Tab pro
   Browser-Origin hält die WebSocket-Verbindungen offen ("Master"); alle
   anderen Tabs ("Follower") bekommen die Payloads via BroadcastChannel
   relayed. Damit verschwindet der CLOSED→SUBSCRIBED→CLOSED-Storm aus dem
   S29-Funktionstest.
============================================================================ */

import { isUserEditing, isAnyModalOpen, rerenderActiveTab } from './utils/dom.js';
// S257 — die beiden Themen, die bis hierher an einer SICHT hingen. Sie bleiben
// Eigentümer ihres States (Liste, Payload-Verarbeitung, Ton, Nachlauf); von hier
// kommt nur noch der Zeitpunkt. Siehe die Begründung am Boot-Block unten.
import { startSignalsRealtime,   stopSignalsRealtime }   from './views/admin/table-signals.js';
import { startGuestNotesRealtime, stopGuestNotesRealtime } from './views/admin/table-guest-notes.js';

let _realtimeUnsub = null;
let _ordersUnsub = null;
let _bonArchiveUnsub = null;
let _zbonArchiveUnsub = null;
let _tableOccupanciesUnsub = null;  // S60 W4.6
let _roundChannelStatusUnsub = null;  // S139 P5
let _tableSignalsUnsub = null;      // S257
let _tableGuestNotesUnsub = null;   // S257
let _scheduleAssignmentsUnsub = null; // S263
let _scheduleRequestsUnsub = null;    // S264 (O1)
let _scheduleWeeksUnsub = null;       // S264 (O1)
let _realtimeRefreshTimer = null;

export function attachRealtimeSync() {
  // S257 — hier stand ein Früh-Ausstieg auf die VIER ältesten Handles
  // (`if (_realtimeUnsub && _ordersUnsub && _bonArchiveUnsub && _zbonArchiveUnsub) return;`).
  // Jedes später ergänzte Abo darunter wäre bei einem zweiten Aufruf nie mehr
  // entstanden — der Guard hätte mit jeder Erweiterung mitwachsen müssen und tat
  // es zweimal nicht. Die einzelnen `if (!_xUnsub …)`-Prüfungen unten sind
  // ohnehin idempotent und tragen das allein; ein zweiter Aufruf kostet sechs
  // billige Vergleiche.
  if (!window.App || !window.App.auth || !window.App.auth.isAuthenticated()) return;
  if (!window.App.storage || typeof window.App.storage.subscribeReservations !== 'function') return;
  const rid = window.App.getCurrentRestaurantId
    ? window.App.getCurrentRestaurantId()
    : '(unknown)';
  if (!_realtimeUnsub) {
    console.info('[realtime] Reservierungs-Sync aktiviert (tenant=' + rid + ').');
    _realtimeUnsub = window.App.storage.subscribeReservations(payload => {
      try { window.App.events.emit('reservation:realtime', payload); } catch (_) {}
      scheduleRealtimeUiRefresh();
    });
  }
  if (!_ordersUnsub && typeof window.App.storage.subscribeOrders === 'function') {
    console.info('[realtime] Bestellungs-Sync aktiviert (tenant=' + rid + ').');
    _ordersUnsub = window.App.storage.subscribeOrders(payload => {
      try { window.App.events.emit('order:realtime', payload); } catch (_) {}
      scheduleRealtimeUiRefresh();
    });
  }
  // Session 30, Block B — Multi-Device-Live-Sync für Archive.
  if (!_bonArchiveUnsub && typeof window.App.storage.subscribeBonArchive === 'function') {
    console.info('[realtime] Bon-Archiv-Sync aktiviert (tenant=' + rid + ').');
    _bonArchiveUnsub = window.App.storage.subscribeBonArchive(payload => {
      try { window.App.events.emit('bonarchive:realtime', payload); } catch (_) {}
      scheduleRealtimeUiRefresh();
    });
  }
  if (!_zbonArchiveUnsub && typeof window.App.storage.subscribeZBonArchive === 'function') {
    console.info('[realtime] Z-Bon-Archiv-Sync aktiviert (tenant=' + rid + ').');
    _zbonArchiveUnsub = window.App.storage.subscribeZBonArchive(payload => {
      try { window.App.events.emit('zbonarchive:realtime', payload); } catch (_) {}
      scheduleRealtimeUiRefresh();
    });
  }
  // S139 P5 — Multi-Device-Sync für Runden-Kacheln (order_round_channel_status)
  if (!_roundChannelStatusUnsub && typeof window.App.storage.subscribeRoundChannelStatus === 'function') {
    console.info('[realtime] Runden-Status-Sync aktiviert (tenant=' + rid + ').');
    _roundChannelStatusUnsub = window.App.storage.subscribeRoundChannelStatus(payload => {
      try { window.App.events.emit('order:round_status:realtime', payload); } catch (_) {}
      scheduleRealtimeUiRefresh();
    });
  }
  // S60 W4.6 — Multi-Device-Sync für Walk-Ins (table_occupancies)
  if (!_tableOccupanciesUnsub && typeof window.App.storage.subscribeTableOccupancies === 'function') {
    console.info('[realtime] Walk-In-Sync aktiviert (tenant=' + rid + ').');
    _tableOccupanciesUnsub = window.App.storage.subscribeTableOccupancies(payload => {
      try { window.App.events.emit('walkin:realtime', payload); } catch (_) {}
      scheduleRealtimeUiRefresh();
    });
  }
  // S257 — Tisch-Signale und Küchen-Hinweise. Die beiden hingen bis hierher an
  // der ersten Sicht, die sie brauchte (`mountSignalBand()` / `initGuestNotes()`
  // in kds/orders/bar/occupancy). Der Tab-Coordinator hält aber genau EINEN
  // Channel je Browser, und zwar im MASTER-Tab: stand der Master auf dem
  // Dashboard, gab es den Channel nirgends, und kein Follower bekam je ein
  // Ereignis. Live gemessen: ein Service-Ruf stand nach 35 s nicht im Band, ein
  // geänderter Küchen-Hinweis blieb über vier Minuten alt.
  //
  // Die Module bleiben Eigentümer ihres States — hier wird nur der ZEITPUNKT
  // entschieden. Die Payload-Verarbeitung (inkl. „Ton nur beim neuen Ruf, nicht
  // beim Erledigen") bleibt genau einmal im jeweiligen Modul stehen; sie hierher
  // zu kopieren hiesse, dieselbe Regel zweimal zu pflegen.
  if (!_tableSignalsUnsub) {
    _tableSignalsUnsub = startSignalsRealtime();
    if (_tableSignalsUnsub) console.info('[realtime] Tisch-Signal-Sync aktiviert (tenant=' + rid + ').');
  }
  if (!_tableGuestNotesUnsub) {
    _tableGuestNotesUnsub = startGuestNotesRealtime();
    if (_tableGuestNotesUnsub) console.info('[realtime] Küchen-Hinweis-Sync aktiviert (tenant=' + rid + ').');
  }
  // S263 — Dienstplan-Zuweisungen: beim Boot abonniert (S257-Lesson: ein Abo,
  // das an einer Sicht hängt, existiert im Master-Tab nur, wenn genau diese
  // Sicht dort offen ist). Die Maske reagiert über die Live-View-Registry auf
  // 'schedule:realtime' (sichtbar → Reload + Zellen-Patch, sonst dirty).
  if (!_scheduleAssignmentsUnsub && typeof window.App.storage.subscribeScheduleAssignments === 'function') {
    _scheduleAssignmentsUnsub = window.App.storage.subscribeScheduleAssignments(payload => {
      try { window.App.events.emit('schedule:realtime', payload); } catch (_) {}
    });
    if (_scheduleAssignmentsUnsub) console.info('[realtime] Dienstplan-Sync aktiviert (tenant=' + rid + ').');
  }
  // S264 (O1) — Abgaben + Wochen-Status: dieselbe Registry-Reaktion wie die
  // Zuweisungen ('schedule:realtime' → Reload + Zellen-/Kopf-Patch).
  if (!_scheduleRequestsUnsub && typeof window.App.storage.subscribeScheduleRequests === 'function') {
    _scheduleRequestsUnsub = window.App.storage.subscribeScheduleRequests(payload => {
      try { window.App.events.emit('schedule:realtime', payload); } catch (_) {}
    });
  }
  if (!_scheduleWeeksUnsub && typeof window.App.storage.subscribeScheduleWeeks === 'function') {
    _scheduleWeeksUnsub = window.App.storage.subscribeScheduleWeeks(payload => {
      try { window.App.events.emit('schedule:realtime', payload); } catch (_) {}
    });
  }
}

export function detachRealtimeSync() {
  // S257 — zuerst die beiden Modul-Abos, damit ein Logout keinen Channel
  // hinterlässt, der in ein abgemeldetes Modul hinein zustellt.
  if (_tableSignalsUnsub) {
    try { _tableSignalsUnsub(); } catch (_) {}
    _tableSignalsUnsub = null;
    console.info('[realtime] Tisch-Signal-Sync deaktiviert.');
  }
  if (_tableGuestNotesUnsub) {
    try { _tableGuestNotesUnsub(); } catch (_) {}
    _tableGuestNotesUnsub = null;
    console.info('[realtime] Küchen-Hinweis-Sync deaktiviert.');
  }
  if (_scheduleAssignmentsUnsub) {
    try { _scheduleAssignmentsUnsub(); } catch (_) {}
    _scheduleAssignmentsUnsub = null;
    console.info('[realtime] Dienstplan-Sync deaktiviert.');
  }
  if (_scheduleRequestsUnsub) { try { _scheduleRequestsUnsub(); } catch (_) {} _scheduleRequestsUnsub = null; }
  if (_scheduleWeeksUnsub)    { try { _scheduleWeeksUnsub(); }    catch (_) {} _scheduleWeeksUnsub = null; }
  if (_roundChannelStatusUnsub) {
    try { _roundChannelStatusUnsub(); } catch (_) {}
    _roundChannelStatusUnsub = null;
  }
  if (_tableOccupanciesUnsub) {
    try { _tableOccupanciesUnsub(); } catch (_) {}
    _tableOccupanciesUnsub = null;
  }
  if (_realtimeUnsub) {
    try { _realtimeUnsub(); } catch (_) {}
    _realtimeUnsub = null;
    console.info('[realtime] Reservierungs-Sync deaktiviert.');
  }
  if (_ordersUnsub) {
    try { _ordersUnsub(); } catch (_) {}
    _ordersUnsub = null;
    console.info('[realtime] Bestellungs-Sync deaktiviert.');
  }
  if (_bonArchiveUnsub) {
    try { _bonArchiveUnsub(); } catch (_) {}
    _bonArchiveUnsub = null;
    console.info('[realtime] Bon-Archiv-Sync deaktiviert.');
  }
  if (_zbonArchiveUnsub) {
    try { _zbonArchiveUnsub(); } catch (_) {}
    _zbonArchiveUnsub = null;
    console.info('[realtime] Z-Bon-Archiv-Sync deaktiviert.');
  }
}

/**
 * Re-Subscribe nach einem Tenant-Wechsel (F-04, Session 8.3). Der
 * Realtime-Channel-Filter wird beim Subscribe einmal eingefroren
 * (`restaurant_id=eq.<id>`), daher müssen wir ihn bei einem Tenant-Wechsel
 * vollständig schließen und mit der neuen ID neu öffnen. Wird vom
 * 'auth:state-changed'-Event-Handler in bootstrap.js getriggert, wenn
 * tenantSwitched=true ist.
 */
export function restartRealtimeSync() {
  detachRealtimeSync();
  attachRealtimeSync();
}

// S60 PERF.2 — Adaptive Debouncing: bei Event-Bursts (>3 Events in 1s)
// Debounce-Fenster auf 1000ms erhöhen statt der normalen 250ms. Reduziert
// Vollständig-Rebuilds bei Stoßzeit-Verkehr drastisch.
const _eventBurstWindow = [];           // Zeitstempel der letzten Events (für Burst-Detection)
const _EVENT_BURST_MAX = 3;             // Mehr als N Events in BURST_WINDOW = Burst-Mode
const _EVENT_BURST_WINDOW_MS = 1000;
const _DEBOUNCE_NORMAL_MS = 250;
const _DEBOUNCE_BURST_MS = 1000;

export function scheduleRealtimeUiRefresh() {
  // Event-Burst-Detection
  const now = Date.now();
  _eventBurstWindow.push(now);
  // Alte Einträge aus dem Sliding-Window entfernen
  while (_eventBurstWindow.length > 0 && _eventBurstWindow[0] < now - _EVENT_BURST_WINDOW_MS) {
    _eventBurstWindow.shift();
  }
  const isBurst = _eventBurstWindow.length > _EVENT_BURST_MAX;
  const delay = isBurst ? _DEBOUNCE_BURST_MS : _DEBOUNCE_NORMAL_MS;

  // S60 PERF.2 — Visibility-API: bei verstecktem Tab keine Refreshes scheduling.
  // Wenn Tab wieder aktiv wird, gibt es 1 Catch-Up-Refresh via visibilitychange-Handler.
  if (typeof document !== 'undefined' && document.hidden) {
    return; // pausieren — visibility-Handler triggert Catch-Up beim Sichtbarwerden
  }

  if (_realtimeRefreshTimer) {
    // Bereits ein Refresh geplant — nur verlängern wenn Burst neu erkannt wurde
    if (isBurst) {
      clearTimeout(_realtimeRefreshTimer);
      _realtimeRefreshTimer = setTimeout(doRealtimeUiRefresh, delay);
    }
    return;
  }
  _realtimeRefreshTimer = setTimeout(doRealtimeUiRefresh, delay);
}

function doRealtimeUiRefresh() {
  _realtimeRefreshTimer = null;
  try {
    const adminEl = document.getElementById('view-admin');
    const adminVisible = adminEl && adminEl.style.display !== 'none';
    if (!adminVisible) return;
    // Page-Visibility: bei verstecktem Tab nicht rendern
    if (typeof document !== 'undefined' && document.hidden) return;
    if (isUserEditing() || isAnyModalOpen()) {
      _realtimeRefreshTimer = setTimeout(doRealtimeUiRefresh, 3000);
      return;
    }
    rerenderActiveTab();
  } catch (e) {
    console.error('[realtime] UI-Refresh fehlgeschlagen:', e);
  }
}

// S60 PERF.2 — Visibility-Listener (einmalig installiert): bei Tab-Wieder-Sichtbar
// genau 1 Catch-Up-Refresh planen, damit verpasste Realtime-Events nachgeholt werden.
let _visibilityListenerInstalled = false;
function installVisibilityListener() {
  if (_visibilityListenerInstalled || typeof document === 'undefined') return;
  _visibilityListenerInstalled = true;
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      // Tab wieder sichtbar — 1 Catch-Up
      if (!_realtimeRefreshTimer) {
        _realtimeRefreshTimer = setTimeout(doRealtimeUiRefresh, 100);
      }
    }
  });
}
if (typeof document !== 'undefined') installVisibilityListener();

// Backward-Compat
if (typeof window !== 'undefined') {
  Object.assign(window, {
    attachRealtimeSync,
    detachRealtimeSync,
    restartRealtimeSync,
    scheduleRealtimeUiRefresh
  });
}

/* =============================================================================
   Test-Hooks (Phase 27.3, Session 34 Block B)
   =============================================================================
   Additive named exports der Pure-Function-Helpers + Module-State-Reader,
   damit die Test-Suite die UI-Refresh-Logik isoliert prüfen kann — ohne
   vi.useFakeTimers-Round-Trip durch setTimeout (vier vi.mock-/setTimeout-
   Iterationen scheiterten am Modul-Cache-Edge-Case in vitest 2.x).

   Memory-Lesson `feedback_gg_hard_way`: additive Test-Exports am Modul-Ende
   statt Reflection auf private Symbols. Zur Laufzeit No-Op (additiv).
============================================================================ */

export {
  doRealtimeUiRefresh as _doRealtimeUiRefresh
};

/** Test-Hook: prüft, ob aktuell ein Refresh-Timer gesetzt ist. */
export function _hasRefreshTimerScheduledForTests() {
  return _realtimeRefreshTimer !== null;
}

/** Test-Hook: räumt den Refresh-Timer + alle Unsub-Refs sauber ab. */
export function _resetRealtimeStateForTests() {
  if (_realtimeRefreshTimer) {
    clearTimeout(_realtimeRefreshTimer);
    _realtimeRefreshTimer = null;
  }
  _realtimeUnsub = null;
  _ordersUnsub = null;
  _bonArchiveUnsub = null;
  _zbonArchiveUnsub = null;
  _roundChannelStatusUnsub = null;
  // S257 — `_tableOccupanciesUnsub` fehlte hier seit S60: ein Test, der attach
  // fuhr, hinterliess das Handle für den nächsten. Mit den zwei neuen wäre
  // dieselbe Lücke sofort wieder da gewesen.
  _tableOccupanciesUnsub = null;
  _tableSignalsUnsub = null;
  _tableGuestNotesUnsub = null;
  _scheduleAssignmentsUnsub = null;   // S263
  _scheduleRequestsUnsub = null;      // S264
  _scheduleWeeksUnsub = null;         // S264
  // Die beiden Module halten ihr Abo selbst — ein Reset, der nur hier nullt,
  // liesse es im Modul stehen und der nächste Test sähe ein Abo aus dem
  // vorigen.
  try { stopSignalsRealtime(); } catch (_) {}
  try { stopGuestNotesRealtime(); } catch (_) {}
}
